import React, { useState, useMemo, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/UserManagement.css";
import {
  FaUserCog,
  FaIdBadge,
  FaEye,
  FaChevronDown,
  FaArrowRight,
  FaTimes,
} from "react-icons/fa";
import { getUserData } from "../utils/authStorage";
import { createOrgUser, updateOrgUser } from "../api/services/usersService";
import { useToast } from "../context/ToastContext";

const DEPARTMENT_OPTIONS = [
  { label: "Engineering", value: "Engineering" },
  { label: "Finance", value: "Finance" },
  { label: "Operations", value: "Operations" },
];

const ROLE_OPTIONS = [
  { label: "Super Admin", value: "super_admin", icon: FaIdBadge },
  { label: "Moderator", value: "moderator", icon: FaUserCog },
  { label: "Auditor", value: "auditor", icon: FaEye },
];

const INITIAL_FORM = {
  username: "",
  name: "",
  department: "Engineering",
  roles: ["super_admin"],
};

export default function CreateUser() {
  const [loading, setLoading] = useState(false);
  const [departmentMenuOpen, setDepartmentMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const departmentMenuRef = useRef(null);
  const { showSuccess, showError } = useToast();

  const mode = location.state?.mode || "create";
  const initialData = location.state?.user || {};

  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";
  const isCreateMode = mode === "create";

  useEffect(() => {
    const handleDocMouseDown = (e) => {
      if (departmentMenuRef.current && !departmentMenuRef.current.contains(e.target)) {
        setDepartmentMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, []);

  // Prepare initial form values
  const formInitialValues = useMemo(() => {
    if (isEditMode || isViewMode) {
      const currentRole = Array.isArray(initialData.roles) && initialData.roles.length
        ? initialData.roles[0]
        : INITIAL_FORM.roles[0];
      const normalizedRole = ROLE_OPTIONS.some((role) => role.value === currentRole)
        ? currentRole
        : INITIAL_FORM.roles[0];

      return {
        ...INITIAL_FORM,
        username: initialData.username || initialData.email || "",
        name: initialData.name || "",
        department: initialData.department || INITIAL_FORM.department,
        roles: [normalizedRole],
      };
    }
    return INITIAL_FORM;
  }, [isEditMode, isViewMode, initialData]);

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .email("Enter a valid email address")
      .required("Enterprise email is required"),

    name: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "Only letters and spaces allowed")
      .required("Full name is required"),

    department: Yup.string().required("Department is required"),

    roles: Yup.array()
      .of(Yup.string().oneOf(ROLE_OPTIONS.map((role) => role.value), "Invalid role"))
      .min(1, "Select an initial role")
      .required("Initial role is required"),
  });

  // Handle form submission
  const handleSubmit = async (values, { resetForm }) => {
    if (isViewMode) return;
const userData = getUserData();
    const user = userData?.user || userData?.data || userData || {};
    const orgId = user?.profile.orgId;
    setLoading(true);
    try {
      const payload = {
        username: values.username.trim(),
        email: values.username.trim(),
        name: values.name.trim(),
        orgID:orgId,
        department: values.department,
        roles: values.roles,
      };
      const updated_payload = {
        email: values.username.trim(),
        name: values.name.trim(),
        department: values.department,
        roles: values.roles,
      };
      console.log(
        "hellofsd",
        isCreateMode ? JSON.stringify(payload, null, 2) : updated_payload
      );
      const result = isCreateMode
        ? await createOrgUser(payload)
        : await updateOrgUser(initialData._id, updated_payload);
      console.log("result", result);
      if (result) {
        showSuccess("Success", result.message || "User saved successfully");
        if (result.isSuccess) {
          navigate("/dashboard/userslist");
        }
      }
    } catch (err) {
      console.log("Error saving user:", err);
      showError("Error", "Something went wrong while saving user.", 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="msd-container um-modalpage">
        <Formik
          initialValues={formInitialValues}
          validationSchema={isViewMode ? null : validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form className="um-adddialog-shell">
              <div className="um-adddialog">
                <div className="um-adddialog__header">
                  <div>
                    <h2 className="um-adddialog__title">{isViewMode ? "View User" : isEditMode ? "Edit User" : "Add User"}</h2>
                    <div className="um-adddialog__subtitle">{isViewMode ? "Review administrative access for this user." : isEditMode ? "Update administrative access to your organization." : "Grant administrative access to your organization."}</div>
                  </div>
                  <button type="button" className="um-adddialog__close" onClick={() => navigate("/dashboard/userslist")}>
                    <FaTimes />
                  </button>
                </div>

                <div className="um-adddialog__steps">
                  <div className="um-adddialog__step is-active">
                    <span className="um-adddialog__stepIndex">1</span>
                    <span>Basic Info</span>
                  </div>
                  <div className="um-adddialog__step">
                    <span className="um-adddialog__stepIndex">2</span>
                    <span>Permission Assignment</span>
                  </div>
                </div>

                <div className="um-adddialog__body">
                  <div className="um-adddialog__grid">
                    <div className="um-adddialog__field">
                      <label className="um-adddialog__label" htmlFor="name">Full Name</label>
                      <Field id="name" name="name" type="text" className="um-adddialog__input" placeholder="e.g. Alexander Vance" disabled={isViewMode} />
                      <ErrorMessage name="name" component="div" className="error-text" />
                    </div>

                    <div className="um-adddialog__field">
                      <label className="um-adddialog__label" htmlFor="department">Department</label>
                      <div className="um-formselectwrap" ref={departmentMenuRef}>
                        <button
                          type="button"
                          id="department"
                          disabled={isViewMode}
                          className={departmentMenuOpen ? "um-sortpill um-formselect open" : "um-sortpill um-formselect"}
                          onClick={() => setDepartmentMenuOpen((open) => !open)}
                          aria-haspopup="menu"
                          aria-expanded={departmentMenuOpen}
                        >
                          <span className="um-formselect__value">{values.department}</span>
                          <FaChevronDown className={departmentMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                        </button>

                        {departmentMenuOpen ? (
                          <div className="um-sortmenu um-formselectmenu" role="menu">
                            {DEPARTMENT_OPTIONS.map((option) => (
                              <button
                                key={option.value}
                                type="button"
                                role="menuitem"
                                className={option.value === values.department ? "um-sortmenu__item active" : "um-sortmenu__item"}
                                onClick={() => {
                                  setFieldValue("department", option.value);
                                  setDepartmentMenuOpen(false);
                                }}
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <ErrorMessage name="department" component="div" className="error-text" />
                    </div>
                  </div>

                  <div className="um-adddialog__field um-adddialog__field--full">
                    <label className="um-adddialog__label" htmlFor="username">Enterprise Email</label>
                    <Field id="username" name="username" type="email" className="um-adddialog__input" placeholder="vance@omnigate.enterprise" disabled={isViewMode || isEditMode} />
                    <ErrorMessage name="username" component="div" className="error-text" />
                  </div>

                  <div className="um-adddialog__field um-adddialog__field--full">
                    <label className="um-adddialog__label">Initial Role</label>
                    <div className="um-rolecards">
                      {ROLE_OPTIONS.map((role) => {
                        const Icon = role.icon;
                        const active = values.roles.includes(role.value);

                        return (
                          <button
                            key={role.value}
                            type="button"
                            className={active ? "um-rolecard active" : "um-rolecard"}
                            disabled={isViewMode}
                            onClick={() => setFieldValue("roles", [role.value])}
                          >
                            <span className="um-rolecard__icon"><Icon /></span>
                            <span className="um-rolecard__label">{role.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <ErrorMessage name="roles" component="div" className="error-text" />
                  </div>
                </div>

                <div className="um-adddialog__footer">
                  <button type="button" className="um-adddialog__cancel" onClick={() => navigate("/dashboard/userslist")}>Cancel</button>
                  {!isViewMode ? (
                    <button type="submit" className="um-adddialog__submit" disabled={loading}>
                      <span>{loading ? "Saving..." : isEditMode ? "Update User" : "Create User"}</span>
                      <FaArrowRight />
                    </button>
                  ) : null}
                </div>
              </div>
            </Form>
          )}
        </Formik>
    </div>
  );
}
